/**
 * Cliente HTTP del realm admin (implementation-notes §4, contracts/admin-api.md).
 *
 * - `baseUrl = import.meta.env.VITE_API_ORIGIN ?? ''` (vacío en local: el proxy de Vite manda
 *   `/api` a http://localhost:3000; absoluto en producción).
 * - Cookie de sesión `fermin_admin_session` → `credentials: 'include'`.
 * - Mutaciones llevan `X-Requested-With: fermin-admin` (header anti-CSRF).
 * - Cada respuesta 2xx se valida con el esquema Zod de `@fermin/contracts` que recibe el método;
 *   `204` (y cuerpo vacío) devuelven `undefined`. Sustituye a `openapi-fetch` (research R-12).
 * - Los errores se normalizan a `ApiError` desde `ErrorEnvelopeSchema`.
 */
import { ErrorEnvelopeSchema, type ErrorCode } from '@fermin/contracts';

import type { z } from 'zod';

/** Prefijo del realm admin. Los métodos reciben rutas relativas a él: `api.get('/me', …)`. */
export const ADMIN_API_PREFIX = '/api/v1/admin';

/** Header anti-CSRF exigido por la API en toda mutación (implementation-notes §2). */
export const CSRF_HEADER = 'X-Requested-With';
export const CSRF_HEADER_VALUE = 'fermin-admin';

/** El shell escucha este evento para llevar al usuario a `/ingresar`. */
export const UNAUTHENTICATED_EVENT = 'fermin:unauthenticated';
/** El proveedor de toasts escucha este evento para avisar que la empresa está en solo lectura. */
export const READ_ONLY_EVENT = 'fermin:read-only';

export interface ApiErrorEventDetail {
  code: ErrorCode;
  message: string;
  requestId: string;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

const MUTATING_METHODS: readonly HttpMethod[] = ['POST', 'PUT', 'PATCH', 'DELETE'];

/** Error normalizado del realm admin: el sobre de `contracts/errors.md` como excepción tipada. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string;

  constructor(init: {
    code: ErrorCode;
    message: string;
    status: number;
    details?: Record<string, unknown> | undefined;
    requestId: string;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /** `true` si la API respondió un código conocido del catálogo. */
  is(code: ErrorCode): boolean {
    return this.code === code;
  }
}

export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_ORIGIN ?? '';
}

function buildUrl(path: string): string {
  const suffix = path.startsWith('/api/') ? path : `${ADMIN_API_PREFIX}${path}`;
  return `${getApiBaseUrl()}${suffix}`;
}

function dispatch(name: string, detail: ApiErrorEventDetail): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent<ApiErrorEventDetail>(name, { detail }));
}

/** Construye el `ApiError` a partir de la respuesta; tolera cuerpos que no son el sobre esperado. */
async function toApiError(response: Response): Promise<ApiError> {
  const requestIdHeader = response.headers.get('X-Request-Id') ?? '';
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const parsed = ErrorEnvelopeSchema.safeParse(payload);
  if (parsed.success) {
    const { code, message, details, request_id } = parsed.data.error;
    return new ApiError({
      code,
      message,
      status: response.status,
      details: details as Record<string, unknown> | undefined,
      requestId: request_id || requestIdHeader,
    });
  }

  return new ApiError({
    code: 'INTERNAL_ERROR',
    message: 'Ocurrió un error inesperado. Inténtalo nuevamente.',
    status: response.status,
    details: undefined,
    requestId: requestIdHeader,
  });
}

/**
 * Despacha el evento de sesión caída o de solo lectura que escuchan el shell y los toasts.
 * Exportada para el único `fetch` que no pasa por `request` (`downloadWorkersCsv`, text/csv).
 */
export function notify(error: ApiError): void {
  const detail: ApiErrorEventDetail = {
    code: error.code,
    message: error.message,
    requestId: error.requestId,
  };
  if (error.code === 'UNAUTHENTICATED') {
    dispatch(UNAUTHENTICATED_EVENT, detail);
  }
  if (error.code === 'READ_ONLY') {
    dispatch(READ_ONLY_EVENT, detail);
  }
}

interface RequestOptions {
  body?: unknown;
  schema?: z.ZodType;
  signal?: AbortSignal;
}

async function request(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<unknown> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (MUTATING_METHODS.includes(method)) {
    headers[CSRF_HEADER] = CSRF_HEADER_VALUE;
  }
  const hasBody = options.body !== undefined;
  if (hasBody) {
    headers['Content-Type'] = 'application/json';
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  const response = await fetch(buildUrl(path), init);

  if (!response.ok) {
    const error = await toApiError(response);
    notify(error);
    throw error;
  }

  if (response.status === 204) {
    return undefined;
  }

  const text = await response.text();
  if (text.length === 0) {
    return undefined;
  }

  const json: unknown = JSON.parse(text);
  return options.schema ? options.schema.parse(json) : undefined;
}

function apiGet<S extends z.ZodType>(
  path: string,
  schema: S,
  options?: { signal?: AbortSignal },
): Promise<z.output<S>> {
  return request('GET', path, { schema, ...options }) as Promise<z.output<S>>;
}

function apiPost<S extends z.ZodType>(path: string, body: unknown, schema: S): Promise<z.output<S>>;
function apiPost(path: string, body?: unknown): Promise<undefined>;
function apiPost(path: string, body?: unknown, schema?: z.ZodType): Promise<unknown> {
  return request('POST', path, { body, ...(schema ? { schema } : {}) });
}

function apiPatch<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S,
): Promise<z.output<S>>;
function apiPatch(path: string, body?: unknown): Promise<undefined>;
function apiPatch(path: string, body?: unknown, schema?: z.ZodType): Promise<unknown> {
  return request('PATCH', path, { body, ...(schema ? { schema } : {}) });
}

/** 009 · `PUT` (upsert de la conexión, `PUT /integration`). */
function apiPut<S extends z.ZodType>(path: string, body: unknown, schema: S): Promise<z.output<S>> {
  return request('PUT', path, { body, schema }) as Promise<z.output<S>>;
}

function apiDelete(path: string): Promise<undefined> {
  return request('DELETE', path) as Promise<undefined>;
}

/** Cliente único del Admin. Toda llamada a la API pasa por aquí. */
export const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
};
